import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  teamMemberService,
  assetService,
  fundingService,
  patentService,
  technologyService
} from '../../utils/entityServices';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { user, loading } = useAuth();
  
  const [productData, setProductData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Associated entities
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [funding, setFunding] = useState([]);
  const [patents, setPatents] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  
  // Analysis completion status
  const [hasProductAnalysis, setHasProductAnalysis] = useState(false);
  const [hasFinancialAnalysis, setHasFinancialAnalysis] = useState(false);
  const [hasPitchDeck, setHasPitchDeck] = useState(false);

  // Fetch product data on component mount
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch product data
      const { data: productResult, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (productError) throw productError;
      setProductData(productResult);
      
      // Fetch financial data if it exists
      const { data: financialResult, error: financialError } = await supabase
        .from('financial_data')
        .select('*')
        .eq('product_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (financialError) throw financialError;
      setFinancialData(financialResult);
      
      // Check if product has analysis data
      const { data: analysisResult, error: analysisError } = await supabase
        .from('product_analysis')
        .select('id')
        .eq('product_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!analysisError && analysisResult) {
        setHasProductAnalysis(true);
      }
      
      // Check if product has financial analysis data
      const { data: financialAnalysisResult, error: financialAnalysisError } = await supabase
        .from('financial_analysis')
        .select('id')
        .eq('product_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!financialAnalysisError && financialAnalysisResult) {
        setHasFinancialAnalysis(true);
      }
      
      // Check if product has pitch deck data
      const { data: pitchDeckResult, error: pitchDeckError } = await supabase
        .from('pitch_decks')
        .select('id')
        .eq('product_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!pitchDeckError && pitchDeckResult) {
        setHasPitchDeck(true);
      }
      
      // Fetch associated entities
      const [
        teamMembersData,
        assetsData,
        fundingData,
        patentsData,
        technologiesData
      ] = await Promise.all([
        teamMemberService.getByProductId(id),
        assetService.getByProductId(id),
        fundingService.getByProductId(id),
        patentService.getByProductId(id),
        technologyService.getByProductId(id)
      ]);
      
      setTeamMembers(teamMembersData);
      setAssets(assetsData);
      setFunding(fundingData);
      setPatents(patentsData);
      setTechnologies(technologiesData);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load product data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  // Helper function to render stage-specific fields
  const renderStageSpecificFields = () => {
    if (!productData) return null;

    // Determine if product is a medical device or a therapeutic
    if (productData.product_category === 'Medical Device') {
      return renderDeviceClassFields();
    }

    const stage = productData.development_stage?.toLowerCase();
    
    const renderField = (label, value) => {
      if (!value) return null;
      return (
        <div className="mb-4">
          <h3 className="font-medium text-gray-700 mb-2">{label}</h3>
          {typeof value === 'string' && value.startsWith('[') ? (
            // Try to parse JSON arrays
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              {JSON.parse(value).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">{value}</p>
          )}
        </div>
      );
    };
    
    // Common fields for most stages
    const commonFields = (
      <>
        {renderField('Target Preclinical PoC Details', productData.target_preclinical_poc_details)}
        {renderField('Preclinical PoC - In Vivo Efficacy', productData.preclinical_poc_efficacy)}
        {renderField('Preclinical PoC - PK/PD', productData.preclinical_poc_pkpd)}
        {renderField('Preclinical PoC - Safety/Toxicity', productData.preclinical_poc_safety)}
        {renderField('Preclinical PoC - Biomarker Validation', productData.preclinical_poc_biomarker)}
        {renderField('Preclinical PoC - Drug Formulation', productData.preclinical_poc_formulation)}
        {renderField('Preclinical PoC - Comparative Studies', productData.preclinical_poc_comparative)}
      </>
    );
    
    // Render different fields based on stage
    switch (stage) {
      case 'discovery':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Discovery Stage Details</h2>
            {renderField('Target Preclinical PoC', productData.target_preclinical_poc)}
            {renderField('Target Preclinical PoC Details', productData.target_preclinical_poc_details)}
          </div>
        );
      
      case 'preclinical':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Preclinical Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {commonFields}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('CMC Plan', productData.cmc_plan)}
                {renderField('Clinical Protocol Plan', productData.clinical_protocol_plan)}
                {renderField('Investigator Plan', productData.investigator_plan)}
                {renderField('eCTD Plan', productData.ectd_plan)}
                {renderField('Pre-IND Meeting Planned Date', productData.pre_ind_meeting_date)}
                {renderField('IND Application Planned Date', productData.ind_application_date)}
                {renderField('IND Approval Planned Date', productData.ind_approval_date)}
              </div>
            </div>
          </div>
        );
      
      case 'phase_i':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Phase I Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {commonFields}
                {renderField('CMC', productData.cmc)}
                {renderField('Phase 1 Study Details', productData.phase1_study_details)}
                {renderField('Phase 1 Study Outcomes', productData.phase1_study_outcomes)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('Clinical PoC Objectives - MOA', productData.clinical_poc_moa)}
                {renderField('Clinical PoC Objectives - Efficacy', productData.clinical_poc_efficacy)}
                {renderField('Clinical PoC Objectives - Dose Range', productData.clinical_poc_dose_range)}
                {renderField('Clinical PoC Objectives - PK/PD', productData.clinical_poc_pkpd)}
                {renderField('Clinical PoC Objectives - Safety', productData.clinical_poc_safety)}
                {renderField('Phase 2 Plan', productData.phase2_plan)}
              </div>
            </div>
          </div>
        );
      
      case 'phase_ii':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Phase II Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {commonFields}
                {renderField('CMC', productData.cmc)}
                {renderField('Phase 1 Study Details', productData.phase1_study_details)}
                {renderField('Phase 1 Study Outcomes', productData.phase1_study_outcomes)}
                {renderField('Phase 2 Study Details', productData.phase2_study_details)}
                {renderField('Clinical PoC Outcomes - MOA', productData.clinical_poc_outcomes_moa)}
                {renderField('Clinical PoC Outcomes - Efficacy', productData.clinical_poc_outcomes_efficacy)}
                {renderField('Clinical PoC Outcomes - Dose Range', productData.clinical_poc_outcomes_dose_range)}
                {renderField('Clinical PoC Outcomes - PK/PD', productData.clinical_poc_outcomes_pkpd)}
                {renderField('Clinical PoC Outcomes - Safety', productData.clinical_poc_outcomes_safety)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('Clinical Phase 3 Objectives - Safety', productData.phase3_objectives_safety)}
                {renderField('Clinical Phase 3 Objectives - Efficacy', productData.phase3_objectives_efficacy)}
                {renderField('Clinical Phase 3 Protocol', productData.phase3_protocol)}
                {renderField('Clinical Phase 3 Management Plans', productData.phase3_management)}
                {renderField('NDA Application Planned Date', productData.nda_application_date)}
                {renderField('NDA Approval Planned Date', productData.nda_approval_date)}
              </div>
            </div>
          </div>
        );
      
      case 'phase_iii':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Phase III Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {commonFields}
                {renderField('CMC', productData.cmc)}
                {renderField('Phase 1 Study Details', productData.phase1_study_details)}
                {renderField('Phase 1 Study Outcomes', productData.phase1_study_outcomes)}
                {renderField('Phase 2 Study Details', productData.phase2_study_details)}
                {renderField('Clinical PoC Outcomes - MOA', productData.clinical_poc_outcomes_moa)}
                {renderField('Clinical PoC Outcomes - Efficacy', productData.clinical_poc_outcomes_efficacy)}
                {renderField('Clinical PoC Outcomes - Dose Range', productData.clinical_poc_outcomes_dose_range)}
                {renderField('Clinical PoC Outcomes - PK/PD', productData.clinical_poc_outcomes_pkpd)}
                {renderField('Clinical PoC Outcomes - Safety', productData.clinical_poc_outcomes_safety)}
                {renderField('Phase 3 Study Details', productData.phase3_study_details)}
                {renderField('Clinical Phase 3 Outcomes - Safety', productData.phase3_outcomes_safety)}
                {renderField('Clinical Phase 3 Outcomes - Efficacy', productData.phase3_outcomes_efficacy)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('CMC Summary Update', productData.cmc_summary)}
                {renderField('Labeling Plan', productData.labeling_plan)}
                {renderField('Risk Management Plan', productData.risk_management_plan)}
                {renderField('eCTD Plan', productData.ectd_plan)}
                {renderField('NDA Application Planned Date', productData.nda_application_date)}
                {renderField('NDA Approval Planned Date', productData.nda_approval_date)}
              </div>
            </div>
          </div>
        );
      
      case 'fda_review':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">FDA Review Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {renderField('Proposed Label', productData.proposed_label)}
                {renderField('Clinical Evidence', productData.clinical_evidence)}
                {renderField('Expected Approval Date', productData.expected_approval_date)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('Market Analysis', productData.market_analysis)}
                {renderField('Marketing Strategy', productData.marketing_strategy)}
                {renderField('Financial Forecast', productData.financial_forecast)}
              </div>
            </div>
          </div>
        );
      
      case 'approved':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Approved Stage Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {renderField('Proposed Label', productData.proposed_label)}
                {renderField('Clinical Evidence', productData.clinical_evidence)}
                {renderField('Approval Date', productData.approval_date)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('Revenue Forecast', productData.revenue_forecast)}
                {renderField('Investment Forecast', productData.investment_forecast)}
                {renderField('Business Strategy', productData.business_strategy)}
                {renderField('Product Valuation', productData.product_valuation)}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Helper function to render device class-specific fields
  const renderDeviceClassFields = () => {
    if (!productData) return null;

    const deviceClass = productData.device_class?.toLowerCase();
    
    const renderField = (label, value) => {
      if (!value) return null;
      return (
        <div className="mb-4">
          <h3 className="font-medium text-gray-700 mb-2">{label}</h3>
          {typeof value === 'string' && value.startsWith('[') ? (
            // Try to parse JSON arrays
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              {JSON.parse(value).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">{value}</p>
          )}
        </div>
      );
    };
    
    // Format device class for display
    // Commented out due to being unused - can be uncommented when needed
    // const formatDeviceClass = (classStr) => {
    //   if (!classStr) return '';
    //   return classStr.charAt(0).toUpperCase() + classStr.slice(1);
    // };
    
    // Render fields based on device class
    switch (deviceClass) {
      case 'class_1':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Class 1 Medical Device Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {renderField('Product Description', productData.product_description)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('501k - Reference Product of Equivalence', productData.reference_501k)}
                {renderField('501k - Demonstration of Substantial Equivalence', productData.demonstration_501k)}
                {renderField('Expected Labeling', productData.expected_labeling)}
                {renderField('eSTAR / ESG Submission Planned Date', productData.submission_date)}
              </div>
            </div>
          </div>
        );
      
      case 'class_2':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Class 2 Medical Device Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {renderField('Product Description', productData.product_description)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('501k - Reference Product of Equivalence', productData.reference_501k)}
                {renderField('501k - Demonstration of Substantial Equivalence', productData.demonstration_501k)}
                {renderField('De Novo - Non-Clinical - Bench Test', productData.de_novo_bench_test)}
                {renderField('De Novo - Non-Clinical - Biocompatibility', productData.de_novo_biocompatibility)}
                {renderField('De Novo - Non-Clinical - Electromagnetic Compatibility', productData.de_novo_emc)}
                {renderField('De Novo - Non-Clinical - Sterilization Validation', productData.de_novo_sterilization)}
                {renderField('De Novo - Non-Clinical - Shelf life validation', productData.de_novo_shelf_life)}
                {renderField('De Novo - Non-Clinical - Human Factors and Usability Studies', productData.de_novo_human_factors)}
                {renderField('De Novo - Non-Clinical - Software Documentation', productData.de_novo_software)}
                {renderField('De Novo - Clinical - Clinical Study Protocol', productData.de_novo_clinical_protocol)}
                {renderField('De Novo - Clinical - Clinical Study Target Results', productData.de_novo_clinical_results)}
                {renderField('De Novo - Clinical - Investigator Information', productData.de_novo_investigator)}
                {renderField('Expected Labeling', productData.expected_labeling)}
                {renderField('eSTAR / ESG Submission Planned Date', productData.submission_date)}
              </div>
            </div>
          </div>
        );
      
      case 'class_3':
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Class 3 Medical Device Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Assets</h3>
                {renderField('Product Description', productData.product_description)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Plans</h3>
                {renderField('Non-Clinical - Bench Test', productData.non_clinical_bench_test)}
                {renderField('Non-Clinical - Biocompatibility', productData.non_clinical_biocompatibility)}
                {renderField('Non-Clinical - Electromagnetic Compatibility', productData.non_clinical_emc)}
                {renderField('Non-Clinical - Sterilization Validation', productData.non_clinical_sterilization)}
                {renderField('Non-Clinical - Shelf life validation', productData.non_clinical_shelf_life)}
                {renderField('Non-Clinical - Human Factors and Usability Studies', productData.non_clinical_human_factors)}
                {renderField('Non-Clinical - Software Documentation', productData.non_clinical_software)}
                {renderField('Clinical - Clinical Study Protocol', productData.clinical_protocol)}
                {renderField('Clinical - Clinical Study Target Results', productData.clinical_results)}
                {renderField('Clinical - Investigator Information', productData.clinical_investigator)}
                {renderField('Expected Labeling', productData.expected_labeling)}
                {renderField('eSTAR / ESG Submission Planned Date', productData.submission_date)}
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Medical Device Details</h2>
            <p className="text-gray-600">No class-specific details available.</p>
          </div>
        );
    }
  };

  if (loading || isLoading) {
    return <LoadingScreen message="Loading product details..." />;
  }
  
  if (error || !productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error || 'Product not found. Please check the URL and try again.'}</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/products" className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Products
        </Link>
      </div>
      
      <div className="flex flex-wrap justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 sm:mb-0">{productData.name}</h1>
        <div className="flex flex-wrap space-x-3">
          {productData.product_category === 'Medical Device' ? (
            <Link to={`/products/${id}/details/device/${productData.device_class?.toLowerCase()}`} className="btn-primary-outline">
              Edit Product Details
            </Link>
          ) : (
            <Link
              to={`/products/${id}/details/${productData.development_stage?.toLowerCase().replace('phase_i', 'phase_i').replace('phase_ii', 'phase_ii').replace('phase_iii', 'phase_iii').replace('fda_review', 'fda_review')}`} 
              className="btn-primary-outline"
            >
              Edit Product Details
            </Link>
          )}
          <Link to={`/products/${id}/team-assets`} className="btn-primary-outline">
            Manage Resources
          </Link>
        </div>
      </div>
      
      {/* Status Tracker */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Product Development Status</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-1 ${hasProductAnalysis ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span className="text-sm font-medium">Product Analysis</span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-1 ${hasFinancialAnalysis ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span className="text-sm font-medium">Financial Analysis</span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-1 ${hasPitchDeck ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span className="text-sm font-medium">Pitch Deck</span>
          </div>
        </div>
      </div>
      
      {/* Product Details */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Product Details</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-700 mb-2">Product Category</h3>
            <p className="text-gray-600 mb-4">{productData.product_category || 'Not specified'}</p>
            
            <h3 className="font-medium text-gray-700 mb-2">Development Stage</h3>
            <p className="text-gray-600 mb-4">{productData.development_stage || 'Not specified'}</p>
            
            <h3 className="font-medium text-gray-700 mb-2">Concept Description</h3>
            <p className="text-gray-600 mb-4">{productData.concept_description || 'Not specified'}</p>
            
            <h3 className="font-medium text-gray-700 mb-2">Indications of Use</h3>
            <p className="text-gray-600 mb-4">{productData.indications_of_use || 'Not specified'}</p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-700 mb-2">Concept Validation Method</h3>
            <p className="text-gray-600 mb-4">{productData.concept_validation_method || 'Not specified'}</p>
            
            {productData.concept_validation_details && (
              <>
                <h3 className="font-medium text-gray-700 mb-2">Concept Validation Details</h3>
                <p className="text-gray-600 mb-4">{productData.concept_validation_details}</p>
              </>
            )}
            
            <h3 className="font-medium text-gray-700 mb-2">Created</h3>
            <p className="text-gray-600 mb-4">
              {productData.created_at ? new Date(productData.created_at).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
        </div>
        
        {/* Render stage-specific fields based on development stage */}
        {renderStageSpecificFields()}
      </div>
      
      {/* Product Analysis Section */}
      {hasProductAnalysis ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Product Analysis</h2>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
              Completed
            </span>
          </div>
          <p className="text-gray-600 mb-4">
            The product analysis has been completed. This includes market size assessment, competitor analysis, 
            development timeline, and success probability for each step.
          </p>
          <Link to={`/products/${id}/analysis`} className="text-primary-600 hover:text-primary-700 text-sm">
            View Full Analysis
          </Link>
        </div>
      ) : null}
      
      {/* Financial Details */}
      {financialData && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Financial Details</h2>
            <div className="flex items-center">
              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${hasFinancialAnalysis ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} mr-2`}>
                {hasFinancialAnalysis ? 'Analysis Completed' : 'Parameters Entered'}
              </span>
              <Link to={`/products/${id}/${hasFinancialAnalysis ? 'financialanalysis' : 'financial'}`} className="text-primary-600 hover:text-primary-700 text-sm">
                {hasFinancialAnalysis ? 'View Analysis' : 'Complete Analysis'}
              </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Overall Project Risk</h3>
              <p className="text-gray-600 mb-4">{financialData.overall_project_risk || 'Not specified'}</p>
              
              <h3 className="font-medium text-gray-700 mb-2">Discount Rate</h3>
              <p className="text-gray-600 mb-4">{financialData.discount_rate ? `${financialData.discount_rate}%` : 'Not specified'}</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Duration of Exclusivity</h3>
              <p className="text-gray-600 mb-4">{financialData.duration_of_exclusivity ? `${financialData.duration_of_exclusivity} years` : 'Not specified'}</p>
              
              <h3 className="font-medium text-gray-700 mb-2">Gross Profit Margin</h3>
              <p className="text-gray-600 mb-4">{financialData.gross_profit_margin ? `${financialData.gross_profit_margin}%` : 'Not specified'}</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">R&D Costs to Preclinical POC</h3>
              <p className="text-gray-600 mb-4">
                {financialData.rd_costs_to_preclinical_poc ? 
                  `$${financialData.rd_costs_to_preclinical_poc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                  'Not specified'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Pitch Deck Section */}
      {hasPitchDeck ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Pitch Deck</h2>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
              Completed
            </span>
          </div>
          <p className="text-gray-600 mb-4">
            Your professional pitch deck is ready. It includes slides covering market opportunity, product features, 
            technology, competition, development plans, team, and funding requirements.
          </p>
          <div className="flex space-x-3">
            <Link to={`/products/${id}/pitchdeckview`} className="text-primary-600 hover:text-primary-700 text-sm">
              View Pitch Deck
            </Link>
            <Link to={`/products/${id}/uploadpitchdeck`} className="text-primary-600 hover:text-primary-700 text-sm">
              Replace with Upload
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Pitch Deck</h2>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
              Not Created
            </span>
          </div>
          <p className="text-gray-600 mb-4">
            Create a professional pitch deck to showcase your product to potential investors and partners.
            You can either generate a pitch deck based on your product details or upload your own.
          </p>
          <div className="flex space-x-3">
            <Link to={`/products/${id}/pitchdeck`} className="text-primary-600 hover:text-primary-700 text-sm">
              Generate Pitch Deck
            </Link>
            <Link to={`/products/${id}/uploadpitchdeck`} className="text-primary-600 hover:text-primary-700 text-sm">
              Upload Pitch Deck
            </Link>
          </div>
        </div>
      )}
      
      {/* Associated Resources */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Team Members */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Team Members</h2>
            <span className="text-gray-500 text-sm">{teamMembers.length} total</span>
          </div>
          
          {teamMembers.length === 0 ? (
            <p className="text-gray-500">No team members associated with this product.</p>
          ) : (
            <div className="space-y-4">
              {teamMembers.map(member => (
                <div key={member.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-700">{member.name}</h3>
                  <p className="text-gray-600 text-sm">{member.title}</p>
                  {member.credentials && (
                    <p className="text-gray-500 text-sm mt-1">{member.credentials}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Technologies */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Technology Platforms</h2>
            <span className="text-gray-500 text-sm">{technologies.length} total</span>
          </div>
          
          {technologies.length === 0 ? (
            <p className="text-gray-500">No technology platforms associated with this product.</p>
          ) : (
            <div className="space-y-4">
              {technologies.map(tech => (
                <div key={tech.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-700">{tech.name}</h3>
                  {tech.description && (
                    <p className="text-gray-500 text-sm mt-1">{tech.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Assets */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Assets</h2>
            <span className="text-gray-500 text-sm">{assets.length} total</span>
          </div>
          
          {assets.length === 0 ? (
            <p className="text-gray-500">No assets associated with this product.</p>
          ) : (
            <div className="space-y-4">
              {assets.map(asset => (
                <div key={asset.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-700">{asset.name}</h3>
                  <p className="text-gray-600 text-sm">{asset.asset_type}</p>
                  {asset.description && (
                    <p className="text-gray-500 text-sm mt-1">{asset.description}</p>
                  )}
                  {(asset.cost || asset.expense) && (
                    <div className="mt-1 flex space-x-4 text-sm">
                      {asset.cost && (
                        <p className="text-gray-500">
                          Cost: ${asset.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                      {asset.expense && (
                        <p className="text-gray-500">
                          Expense: ${asset.expense.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Patents */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Patents</h2>
            <span className="text-gray-500 text-sm">{patents.length} total</span>
          </div>
          
          {patents.length === 0 ? (
            <p className="text-gray-500">No patents associated with this product.</p>
          ) : (
            <div className="space-y-4">
              {patents.map(patent => (
                <div key={patent.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-700">{patent.name}</h3>
                  {patent.patent_number && (
                    <p className="text-gray-600 text-sm">Patent #{patent.patent_number}</p>
                  )}
                  {patent.description && (
                    <p className="text-gray-500 text-sm mt-1">{patent.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Funding */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Funding</h2>
            <span className="text-gray-500 text-sm">{funding.length} total</span>
          </div>
          
          {funding.length === 0 ? (
            <p className="text-gray-500">No funding associated with this product.</p>
          ) : (
            <div className="space-y-4">
              {funding.map(fund => (
                <div key={fund.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-700">{fund.name}</h3>
                  <p className="text-gray-600 text-sm">{fund.source}</p>
                  {fund.objective && (
                    <p className="text-gray-500 text-sm mt-1">{fund.objective}</p>
                  )}
                  <div className="mt-1 flex flex-wrap space-x-4 text-sm">
                    {fund.amount && (
                      <p className="text-gray-500">
                        Amount: ${fund.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    )}
                    {fund.date_received && (
                      <p className="text-gray-500">
                        Received: {new Date(fund.date_received).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="mt-6 flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
        <Link
          to={`/products/${id}/analysis`}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          Analyze Product
        </Link>
        
        <Link
          to={`/products/${id}/${hasFinancialAnalysis ? 'financialanalysis' : 'financial'}`}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {hasFinancialAnalysis ? 'View Financial Analysis' : 'Financial Analysis'}
        </Link>
        
        {hasPitchDeck && (
          <Link
            to={`/products/${id}/pitchdeckview`}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path>
            </svg>
            View Pitch Deck
          </Link>
        )}
        
        <Link
          to={`/products/${id}/uploadpitchdeck`}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
          </svg>
          {hasPitchDeck ? 'Replace Pitch Deck (PDF)' : 'Upload Pitch Deck (PDF)'}
        </Link>
        
        <Link
          to={`/products/${id}/details/${productData.development_stage.toLowerCase()}`}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
          </svg>
          Edit Product Details
        </Link>
      </div>
    </div>
  );
};

export default ProductDetailPage; 